import JSZip from 'jszip';
import * as Yup from 'yup';
import {useIntl} from 'react-intl';
import {Button, IconButton} from '@material-ui/core';
import {Field, FieldProps, Form, Formik} from 'formik';
import {GetApp as GetAppIcon} from '@material-ui/icons';
import React, {useEffect, useState, useCallback, useMemo} from 'react';

import {downloadBlob} from '../../../utils';
import {ScriptUploadField} from '../Fields/ScriptUploadField';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {StudioPopover} from '../../../base-components/StudioPopover';
import {PreProcessor} from '../../../types/preprocessor/PreProcessor';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {PRE_AND_POST_PROCESSORS_SETTINGS} from '../../../config/constants';
import {getPreProcessorFile} from '../../../api/preprocessor/PreProcessorView';
import {PreProcessorNewResponse} from '../../../api/preprocessor/PreProcessorNew';
import {PreProcessorUpdateRequest} from '../../../api/preprocessor/PreProcessorUpdate';
import {UpdatePreProcessorRequest} from '../../../types/preprocessor/UpdatePreProcessorRequest';
import {
  ParameterFieldArray,
  parameterArrayValidation,
} from '../Fields/ParameterFieldArray';

import '../index.scss';

const defaultTemplate: PreProcessorNewResponse = {
  id: '',
  name: '',
  filePaths: [],
  projectId: '',
  description: '',
  applicationTypeOptions: {},
  transformationType: 'SCRIPT',
  executionContainerOptions: {},
  script: {
    parameters: [],
  },
};

const validationSchema = Yup.object({
  id: Yup.string().required('Required'),
  name: Yup.string().required('Required'),
  projectId: Yup.string().required('Required'),
  filePaths: Yup.object()
    .test('file-exists', 'Required', files => Boolean(files && Object.keys(files).length))
    .test('file-not-empty', 'File is empty', files =>
      Boolean(!files || Object.values(files)?.[0])
    ),
  script: Yup.object().shape({
    parameters: parameterArrayValidation,
    applicationType: Yup.string().required('Required'),
    executionContainer: Yup.string().required('Required'),
  }),
});

type PreProcessingFormProps = {
  title: string;
  projectId: string;
  viewOnly?: boolean;
  returnText?: string;
  fileNames?: string[];
  template?: PreProcessorNewResponse;

  onCancel: () => void;
  onSubmit: (values: PreProcessorUpdateRequest) => Promise<void>;
};

export const PreProcessingForm = ({
  title,
  viewOnly,
  template,
  projectId,
  returnText,
  fileNames: initialFileNames,

  onSubmit,
  onCancel,
}: PreProcessingFormProps) => {
  const intl = useIntl();
  const [scripts, setScripts] = useState<File[]>([]);
  const [filePaths, setFilePaths] = useState<Record<string, string>>({});
  const fileNames = useMemo(() => initialFileNames ?? [], [initialFileNames]);

  const {
    id,
    name,
    executionContainerOptions,
    applicationTypeOptions,
    script,
    description,
    transformations,
  } = template || defaultTemplate;

  const initialValues: UpdatePreProcessorRequest = {
    id,
    name,
    script,
    projectId,
    description,
    filePaths,
    transformations,
    transformationType: 'SCRIPT',
  };

  const getFiles = useCallback(
    async function getFiles() {
      const allFiles: File[] = [];
      const newFilePaths: Record<string, string> = {};

      for (const fileName of fileNames) {
        const content = await getPreProcessorFile(projectId, id, fileName);

        allFiles.push(new File([content.data], fileName));
        newFilePaths[fileName] = await content.data.text();
        setFilePaths(newFilePaths);
      }

      setScripts(prev => [...prev, ...allFiles]);
    },
    [fileNames, projectId, id]
  );

  useEffect(() => {
    getFiles();
  }, [getFiles]);

  const downloadAllFiles = useCallback(
    async function downloadAllFiles() {
      const archive = new JSZip();

      for (let file of scripts) {
        const fileContent = file.slice(0, file.size, file.type);

        archive.file(file.name, fileContent);
      }

      const fileName = 'pre-processor.zip';
      const zipBlob = await archive.generateAsync({type: 'blob'});
      const url = URL.createObjectURL(
        new File([zipBlob], fileName, {type: 'application/zip'})
      );
      const {promise} = downloadBlob({fileName, blobUrl: url});

      await promise;
    },
    [scripts]
  );

  return (
    <Formik
      initialValues={{
        ...initialValues,
        script: {
          ...initialValues.script,
          applicationType: initialValues.script.applicationType,
          executionContainer: initialValues.script.executionContainer,
        },
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange
      enableReinitialize
    >
      {({values, isSubmitting}) => (
        <div className="processing-form">
          <header>
            <Button
              className="processing-form__back"
              variant="contained"
              disableElevation
              onClick={onCancel}
            >
              {returnText ??
                intl.formatMessage({
                  id: 'form.returnToDesign',
                })}
            </Button>
            <div>
              <StudioTooltip
                title={intl.formatMessage({
                  id: 'project.preprocessor.downloadFile',
                })}
              >
                <IconButton onClick={downloadAllFiles}>
                  <GetAppIcon />
                </IconButton>
              </StudioTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                form="pre-processor-form"
                disabled={isSubmitting || viewOnly}
                disableElevation
              >
                {intl.formatMessage({
                  id: isSubmitting ? 'form.submitting' : 'form.submit',
                })}
              </Button>
            </div>
          </header>
          <div className="processing-form__main">
            <h2>{title}</h2>
            <Form id="pre-processor-form" noValidate>
              <div className="processing-form__form-top">
                <Field name="name">
                  {({field, meta}: FieldProps<PreProcessor['name']>) => (
                    <div>
                      <StudioTextField
                        className="processing-form__field"
                        label={
                          <React.Fragment>
                            {intl.formatMessage({id: 'sharedProcessor.form.name'})}
                            <StudioPopover
                              infoMessage={intl.formatMessage({
                                id: 'editPreProcessor.name.tooltip',
                              })}
                            />
                          </React.Fragment>
                        }
                        placeholder={intl.formatMessage({
                          id: 'sharedProcessor.form.name.placeholder',
                        })}
                        InputProps={field}
                        error={Boolean(meta.error && meta.touched)}
                        helperText={meta.error && meta.touched ? meta.error : ''}
                        disabled={viewOnly}
                        inputProps={{
                          maxLength: PRE_AND_POST_PROCESSORS_SETTINGS.name.maxLength,
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="description">
                  {({field}: FieldProps<PreProcessor['description']>) => (
                    <StudioTextField
                      className="processing-form__field"
                      label={
                        <React.Fragment>
                          {intl.formatMessage({id: 'sharedProcessor.form.description'})}
                          <StudioPopover
                            infoMessage={intl.formatMessage({
                              id: 'editPreProcessor.description.tooltip',
                            })}
                          />
                        </React.Fragment>
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.description.placeholder',
                      })}
                      InputProps={field}
                      disabled={viewOnly}
                      multiline={PRE_AND_POST_PROCESSORS_SETTINGS.description.multiline}
                      inputProps={{
                        className: PRE_AND_POST_PROCESSORS_SETTINGS.description.className,
                        maxLength: PRE_AND_POST_PROCESSORS_SETTINGS.description.maxLength,
                      }}
                    />
                  )}
                </Field>
                <Field name="script.applicationType">
                  {({
                    field,
                    meta,
                  }: FieldProps<PreProcessor['script']['applicationType']>) => (
                    <StudioSelect
                      className="processing-form__field"
                      label={intl.formatMessage({id: 'sharedProcessor.form.appType'})}
                      additionalLabel={
                        <StudioPopover
                          infoMessage={intl.formatMessage({
                            id: 'editPreProcessor.applicationType.tooltip',
                          })}
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.appType.placeholder',
                      })}
                      options={applicationTypeOptions}
                      disablePlaceholder
                      SelectProps={{...field, value: field.value || ''}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={viewOnly}
                    />
                  )}
                </Field>
                <Field name="script.executionContainer">
                  {({
                    field,
                    meta,
                  }: FieldProps<PreProcessor['script']['executionContainer']>) => (
                    <StudioSelect
                      className="processing-form__field"
                      label={intl.formatMessage({
                        id: 'sharedProcessor.form.target',
                      })}
                      additionalLabel={
                        <StudioPopover
                          infoMessage={intl.formatMessage({
                            id: 'editPreProcessor.executionContainer.tooltip',
                          })}
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.target.placeholder',
                      })}
                      options={executionContainerOptions}
                      SelectProps={{
                        ...field,
                        value: field.value || '',
                        onChange: e => {
                          field.onChange(e);
                        },
                      }}
                      disablePlaceholder
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={viewOnly}
                    />
                  )}
                </Field>
                <Field name="filePaths">
                  {({
                    field,
                    form,
                    meta,
                  }: FieldProps<PreProcessorUpdateRequest['filePaths']>) => {
                    const handleFileUpload = async (files: File[]) => {
                      const newFilePaths: Record<string, string> = {};

                      for (const file of files) {
                        newFilePaths[file.name] = await file.text();
                      }

                      form.setFieldValue('filePaths', newFilePaths);
                      form.setFieldTouched('filePaths');
                      form.validateField('filePaths');

                      setScripts(files);
                    };

                    const executionContainer = values.script.executionContainer;

                    return executionContainer ? (
                      <ScriptUploadField
                        label={intl.formatMessage({id: 'sharedProcessor.form.script'})}
                        tooltip={intl.formatMessage({
                          id: 'editPreProcessor.filePaths.tooltip',
                        })}
                        className="processing-form__field"
                        filePaths={scripts}
                        onChange={handleFileUpload}
                        errorText={meta.error && meta.touched ? meta.error : ''}
                        disabled={viewOnly}
                      />
                    ) : null;
                  }}
                </Field>
              </div>
              <hr />
              <h5>{intl.formatMessage({id: 'preProcessor.params.header'})}</h5>
              <ParameterFieldArray
                name="script.parameters"
                parameters={values.script.parameters || []}
                disabled={viewOnly}
              />
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
};
